/**
 * Announcement Bar Component
 * ------------------------------------------------------------------------
 * Features:
 * - Responsive grid layout (desktop) / carousel (mobile)
 * - Keyboard navigation (arrow keys)
 * - Screen reader announcements
 * 
 * - TODO: Need to tidy up the logics
 */
class AnnouncementBar extends HTMLElement {
  static MOBILE_BREAKPOINT = 750;
  static DEBOUNCE_DELAY = 250;

  constructor() {
    super();
    
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.visibilityHandler = null;
    
    this.slides = null;
    this.prevButton = null;
    this.nextButton = null;
    
    this.autoplaySpeed = parseInt(this.dataset.autoplay) * 1000 || 0;
    this.blockCount = parseInt(this.dataset.blockCount) || 1;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  

  connectedCallback() {
    if (window.innerWidth >= AnnouncementBar.MOBILE_BREAKPOINT || this.blockCount <= 1) {
      return;
    }
    this.init();
    this.handleResize = this.debounce(this.handleResize, AnnouncementBar.DEBOUNCE_DELAY);
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.slides = this.querySelectorAll('.announcement-bar__slide');
    this.prevButton = this.querySelector('[data-action="prev"]');
    this.nextButton = this.querySelector('[data-action="next"]');

    if (!this.slides.length) return;

    this.setupEventListeners();

    if (this.autoplaySpeed > 0) {
      this.startAutoplay();
    }

    this.setupKeyboardNavigation();
    window.addEventListener('resize', this.handleResize);

    // Ensure only the current slide is visible on init
    this.updateSlide();
  }

  updateSlide() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.setAttribute('aria-hidden', !isActive);

       // Visually hide non-active slides to avoid overlap/fading artifacts
      if (isActive) {
        slide.style.display = 'flex';
        slide.style.opacity = '1';
        slide.style.pointerEvents = '';
      } else {
        slide.style.display = 'none';
        slide.style.opacity = '0';
        slide.style.pointerEvents = 'none';
      }
    });

    // Announce to screen readers
    const liveRegion = this.querySelector('.announcement-bar__live-region');
    if (liveRegion) {
      liveRegion.textContent = `Announcement ${this.currentIndex + 1} of ${this.slides.length}`;
    }
  }

  startAutoplay() {
    if (this.autoplaySpeed <= 0 || window.innerWidth >= AnnouncementBar.MOBILE_BREAKPOINT) return;

    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    if (this.autoplaySpeed > 0) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  handleResize() {
    if (window.innerWidth >= AnnouncementBar.MOBILE_BREAKPOINT) {
      this.cleanup();
      this.slides?.forEach(slide => {
        slide.removeAttribute('aria-hidden');
        // Reset inline styles added for mobile carousel mode
        slide.style.display = '';
        slide.style.opacity = '';
        slide.style.pointerEvents = '';
      });
    } else if (this.blockCount > 1 && !this.slides) {
      this.init();
    }
  }


  /*
    Setup Navigation Buttons
  */
  setupKeyboardNavigation() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.previous();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });
  }

  setupEventListeners() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.previous());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }
    
    // Pause when page is hidden
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplaySpeed > 0) {
        this.startAutoplay();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.updateSlide();
    this.resetAutoplay();
  }

  previous() {
    this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.updateSlide();
    this.resetAutoplay();
  }

  cleanup() {
    this.stopAutoplay();
    
    window.removeEventListener('resize', this.handleResize);
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }
}

// Register the custom element
if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}